import type { JSX } from "react";
import { useState } from "react";

import { ChevronRight } from "lucide-react";

import type { DisplacementAction, EvidenceStrength } from "../types/visibility";
import { LLM_COLORS } from "../utils/llmColors";
import {
  RECOMMENDATION_TYPE_LABELS,
  SCORING_CATEGORY_COLORS,
  SCORING_CATEGORY_LABELS,
} from "../utils/recommendationLabels";
import Chip from "./Chip";
import PriorityBadge from "./PriorityBadge";

const EVIDENCE_STRENGTH_META: Record<EvidenceStrength, { label: string; color: string }> = {
  measured: { label: "Measured", color: "emerald" },
  documented: { label: "Documented", color: "blue" },
  correlational: { label: "Correlational", color: "yellow" },
};

interface VisibilityActionCardProps {
  action: DisplacementAction;
}

/** One displacement action from `explanation.actions`, rendered as a self-contained card. */
const VisibilityActionCard = ({ action }: VisibilityActionCardProps): JSX.Element => {
  const evidenceStrength = EVIDENCE_STRENGTH_META[action.evidence_strength];
  const [rationaleOpen, setRationaleOpen] = useState(false);

  return (
    <div className="p-5 rounded-xl border border-surface-container-high bg-surface-container-low">
      <h4 className="font-bold text-on-surface mb-2">{action.title}</h4>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <PriorityBadge priority={action.impact} />
        <Chip color={SCORING_CATEGORY_COLORS[action.scoring_category] || "neutral"}>
          {SCORING_CATEGORY_LABELS[action.scoring_category] || action.scoring_category}
        </Chip>
        <Chip color="neutral">
          {RECOMMENDATION_TYPE_LABELS[action.recommendation_type] || action.recommendation_type}
        </Chip>
        {evidenceStrength && <Chip color={evidenceStrength.color}>{evidenceStrength.label}</Chip>}
      </div>
      {action.llm_providers.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className="text-xs font-semibold text-on-surface-variant uppercase tracking-wider">
            Improves
          </span>
          {action.llm_providers.map((provider) => {
            const style = LLM_COLORS[provider] || {
              bg: "bg-surface-container-low",
              text: "text-on-surface",
              border: "border-surface-container-high",
              label: provider,
            };
            return (
              <span
                key={provider}
                className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${style.bg} ${style.text} ${style.border}`}
              >
                {style.label}
              </span>
            );
          })}
        </div>
      )}
      <button
        type="button"
        onClick={() => setRationaleOpen((v) => !v)}
        aria-expanded={rationaleOpen}
        className="flex items-center gap-1 text-xs font-semibold text-on-surface-variant uppercase tracking-wider hover:text-on-surface transition-colors"
      >
        <ChevronRight
          className={`w-3.5 h-3.5 transition-transform ${rationaleOpen ? "rotate-90" : ""}`}
        />
        Why this matters
      </button>
      {rationaleOpen && <p className="text-sm text-on-surface-variant mt-2">{action.rationale}</p>}
      {action.example_urls.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-2">
            Pages to target
          </p>
          <ul className="space-y-1">
            {action.example_urls.map((url) => (
              <li key={url}>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={url}
                  className="text-sm text-primary hover:underline break-all"
                >
                  {url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default VisibilityActionCard;
